import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Users, Calendar, Settings, Plus, Edit2, Power, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function AuthImage({ path, alt, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    const loadImage = async () => {
      setFailed(false);
      setSrc(null);
      try {
        const cleanPath = path.replace(/^\/api/, '');
        const response = await api.get(cleanPath, { responseType: 'blob' });
        objectUrl = URL.createObjectURL(response.data);
        if (!cancelled) setSrc(objectUrl);
      } catch (error) {
        console.error('Gagal memuat logo:', error);
        if (!cancelled) setFailed(true);
      }
    };

    if (path) loadImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (failed || !src) return null;

  return <img src={src} alt={alt} className={className} />;
}

export default function TeamsManagement() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    max_admins: 10,
    max_employees: 100,
    file_retention_days: 90
  });

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTeam) {
        await api.put(`/teams/${editingTeam.id}`, formData);
        toast.success('Team updated successfully!');
      } else {
        await api.post('/teams', formData);
        toast.success('Team created successfully!');
      }

      setShowForm(false);
      setEditingTeam(null);
      setFormData({ name: '', max_admins: 10, max_employees: 100, file_retention_days: 90 });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      max_admins: team.max_admins,
      max_employees: team.max_employees,
      file_retention_days: team.file_retention_days
    });
    setShowForm(true);
  };

  const handleDeactivate = async (teamId, action, teamName) => {
    if (action !== 'activate') {
      if (!window.confirm(`Are you sure you want to deactivate team "${teamName}"?`)) {
        return;
      }
    }
    try {
      await api.post(`/teams/${teamId}/deactivate`, { action });
      toast.success(`Team ${action === 'activate' ? 'activated' : 'deactivated'} successfully!`);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleLogoUpload = async (teamId, file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Hanya file gambar yang diperbolehkan');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran logo maksimal 5MB');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/teams/${teamId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded successfully!');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3C34]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="pb-2 border-b border-slate-200/70 flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="teams-page-title">
            Teams Management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Manage all teams and their configurations</p>
        </div>
        <Button
          onClick={() => {
            setShowForm(!showForm);
            setEditingTeam(null);
            setFormData({ name: '', max_admins: 10, max_employees: 100, file_retention_days: 90 });
          }}
          data-testid="create-team-button"
          className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
        >
          <Plus className="w-5 h-5 mr-2" />
          {showForm ? 'Cancel' : 'Create Team'}
        </Button>
      </div>

      {showForm ? (
        /* Create/Edit Form — list disembunyikan selama form terbuka */
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">
              {editingTeam ? 'Edit Team' : 'Create New Team'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {editingTeam ? 'Update konfigurasi team' : 'Isi detail team baru'}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">Team Name *</Label>
                <Input
                  id="name"
                  data-testid="team-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Acme Corp"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="retention" className="text-sm font-medium text-slate-700">File Retention (Days) *</Label>
                <Input
                  id="retention"
                  type="number"
                  min="1"
                  max="365"
                  value={formData.file_retention_days}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') { setFormData({ ...formData, file_retention_days: '' }); return; }
                    const num = parseInt(val, 10);
                    setFormData({ ...formData, file_retention_days: Number.isNaN(num) ? '' : num });
                  }}
                  required
                  className="w-full border-slate-200 rounded-xl text-sm focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxAdmins" className="text-sm font-medium text-slate-700">Max Admins *</Label>
                <Input
                  id="maxAdmins"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_admins}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') { setFormData({ ...formData, max_admins: '' }); return; }
                    const num = parseInt(val, 10);
                    setFormData({ ...formData, max_admins: Number.isNaN(num) ? '' : num });
                  }}
                  required
                  className="w-full border-slate-200 rounded-xl text-sm focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxEmployees" className="text-sm font-medium text-slate-700">Max Employees *</Label>
                <Input
                  id="maxEmployees"
                  type="number"
                  min="1"
                  max="10000"
                  value={formData.max_employees}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') { setFormData({ ...formData, max_employees: '' }); return; }
                    const num = parseInt(val, 10);
                    setFormData({ ...formData, max_employees: Number.isNaN(num) ? '' : num });
                  }}
                  required
                  className="w-full border-slate-200 rounded-xl text-sm focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
              >
                {editingTeam ? 'Update Team' : 'Create Team'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingTeam(null);
                  setFormData({ name: '', max_admins: 10, max_employees: 100, file_retention_days: 90 });
                }}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              >
                Batal
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        /* Teams List */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 text-sm col-span-full">
              No teams yet. Create one above.
            </p>
          ) : (
            teams.map((team) => (
              <Card
                key={team.id}
                className="p-6 bg-white border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`team-card-${team.id}`}
              >
                <div className="space-y-4">
                  {/* Team Logo & Name */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {team.logo_url ? (
                        <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0">
                          <AuthImage
                            path={team.logo_url}
                            alt={team.name}
                            className="w-12 h-12 object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-[#1A3C34]/10 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-[#1A3C34]" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-bold font-heading text-slate-900">{team.name}</h3>
                        <span className={`text-[11px] px-2 py-0.5 rounded-md font-medium ${
                          team.is_active
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                            : 'bg-red-50 text-red-700 border border-red-100'
                        }`}>
                          {team.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">Admins: {team.current_admins}/{team.max_admins}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">Employees: {team.current_employees}/{team.max_employees}</span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">Retention: {team.file_retention_days}d</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(team)}
                      className="flex-1 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.style.display = 'none';
                        input.onchange = (e) => {
                          const file = e.target.files?.[0];
                          if (file) handleLogoUpload(team.id, file);
                          input.remove();
                        };
                        document.body.appendChild(input);
                        input.click();
                      }}
                      className="flex-1 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Logo
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeactivate(team.id, team.is_active ? 'add_1_month' : 'activate', team.name)}
                      className={`rounded-xl border-slate-200 hover:bg-slate-50 ${
                        team.is_active ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'
                      }`}
                    >
                      <Power className="w-4 h-4 mr-1" />
                      {team.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/teams/${team.id}/admins`)}
                    className="w-full rounded-xl text-[#1A3C34] hover:bg-[#1A3C34]/5 hover:text-[#1A3C34]"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Manage Admins
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}